import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { apiFetch, BASE_URL } from '../config/api';

const TaskTypesContext = createContext();

export const useTaskTypes = () => {
  const context = useContext(TaskTypesContext);
  if (!context) {
    throw new Error('useTaskTypes must be used within a TaskTypesProvider');
  }
  return context;
};

export const TaskTypesProvider = ({ children }) => {
  const [taskTypes, setTaskTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTaskTypes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch(`${BASE_URL}/api/task-types/?is_active=true`);
      if (!response.ok) {
        throw new Error('Failed to fetch task types');
      }

      const data = await response.json();
      setTaskTypes(data || []);
    } catch (err) {
      console.error('Error fetching task types:', err);
      setError(err.message);
      // Fallback defaults
      setTaskTypes(getDefaultTaskTypes());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTaskTypes();
  }, [fetchTaskTypes]);

  // Map keyed by task_type_code
  const taskTypeMap = useMemo(() => {
    const map = {};
    taskTypes.forEach(tt => {
      map[tt.task_type_code] = tt;
    });
    return map;
  }, [taskTypes]);

  // Get config for a specific task type code
  const getTaskTypeConfig = useCallback((code) => {
    const tt = taskTypeMap[code];
    if (!tt) return { icon: '', color: '#6c757d', name: code, configTableName: null, specialForm: null };
    return {
      icon: tt.icon || '',
      color: tt.color_hex || '#6c757d',
      name: tt.task_type_name || code,
      configTableName: tt.config_table_name,
      specialForm: tt.special_form || null
    };
  }, [taskTypeMap]);

  // Get options for select dropdowns
  const getTaskTypeOptions = useCallback(() => {
    return taskTypes.map(tt => ({
      value: tt.task_type_code,
      label: tt.task_type_name
    }));
  }, [taskTypes]);

  // Get production task type codes
  const getProductionTaskTypes = useCallback(() => {
    return taskTypes
      .filter(tt => tt.category === 'PRODUCTION')
      .map(tt => tt.task_type_code);
  }, [taskTypes]);

  const value = {
    taskTypes,
    taskTypeMap,
    loading,
    error,
    getTaskTypeConfig,
    getTaskTypeOptions,
    getProductionTaskTypes,
    refreshTaskTypes: fetchTaskTypes
  };

  return (
    <TaskTypesContext.Provider value={value}>
      {children}
    </TaskTypesContext.Provider>
  );
};

// Fallback defaults if API is unavailable
const getDefaultTaskTypes = () => [
  { task_type_code: 'SETUP', task_type_name: 'Setup', icon: '', color_hex: '#007bff', category: 'PRODUCTION', config_table_name: 'workflow_task_setup', special_form: 'SETUP' },
  { task_type_code: 'PRINTING', task_type_name: 'Printing', icon: '', color_hex: '#28a745', category: 'PRODUCTION', config_table_name: 'workflow_task_printing', special_form: 'PRINTING' },
  { task_type_code: 'DIE_CUTTING', task_type_name: 'Die Cutting', icon: '', color_hex: '#fd7e14', category: 'PRODUCTION', config_table_name: 'workflow_task_die_cutting', special_form: 'DIE_CUTTING' },
  { task_type_code: 'LAMINATING', task_type_name: 'Laminating', icon: '', color_hex: '#17a2b8', category: 'PRODUCTION', config_table_name: 'workflow_task_laminating', special_form: 'PRINTING' },
  { task_type_code: 'PACKAGING', task_type_name: 'Packaging', icon: '', color_hex: '#e83e8c', category: 'PRODUCTION', config_table_name: 'workflow_task_packaging', special_form: 'MATERIAL_PREP' },
  { task_type_code: 'MATERIAL_PREP', task_type_name: 'Material Prep', icon: '', color_hex: '#fd7e14', category: 'PRODUCTION', config_table_name: 'workflow_task_material_prep', special_form: 'MATERIAL_PREP' },
  { task_type_code: 'QUALITY_CHECK', task_type_name: 'Quality Check', icon: '', color_hex: '#dc3545', category: 'WORKFLOW', config_table_name: 'workflow_task_quality_check', special_form: 'QUALITY_CHECK' },
  { task_type_code: 'SEAMED', task_type_name: 'Pegado', icon: '🔗', color_hex: '#6f42c1', category: 'PRODUCTION', config_table_name: 'workflow_task_seamed', special_form: 'SEAMED' },
  { task_type_code: 'STANDARD', task_type_name: 'Standard', icon: '', color_hex: '#6c757d', category: 'WORKFLOW', config_table_name: null, special_form: null },
  { task_type_code: 'WORKFLOW', task_type_name: 'Workflow', icon: '', color_hex: '#17a2b8', category: 'WORKFLOW', config_table_name: null, special_form: null },
  { task_type_code: 'AD_HOC', task_type_name: 'Ad Hoc', icon: '', color_hex: '#ffc107', category: 'OTHER', config_table_name: null, special_form: null },
  { task_type_code: 'REWORK', task_type_name: 'Rework', icon: '', color_hex: '#dc3545', category: 'MAINTENANCE', config_table_name: null, special_form: null },
  { task_type_code: 'MAINTENANCE', task_type_name: 'Maintenance', icon: '', color_hex: '#6c757d', category: 'MAINTENANCE', config_table_name: null, special_form: null },
];

export default TaskTypesContext;
